// Shared calendar-entry editor (web). Used both for personal entries (CalendarPage)
// and class-scoped entries pushed to a clan (community SchedulePanel). Mirrors the
// iOS CalendarEntryEditView: three kinds (homework / block / task), create or edit,
// optional clanId scoping. App-blocking is iOS-only — on web a "block" is schedule,
// and a window's per-app set ("activity_selection") is configured on iOS; here we
// only show that it exists and pass it through unchanged on edit.
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  type CalendarEntry,
  type CalendarEntryKind,
  createCalendarEntry,
  deleteCalendarEntry,
  updateCalendarEntry,
} from "@/api/calendar";
import { listQuestionSets } from "@/api/question-sets";
import { Alert, Button, Input, cx } from "@/components/ui";

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export const DAY_LABEL: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

/** One-line schedule summary for a list row. */
export function scheduleText(e: CalendarEntry): string {
  if (e.entry_kind === "block") {
    const days = DAYS.filter((d) => e.days.includes(d)).map((d) => DAY_LABEL[d]).join(", ");
    const win = e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : "";
    const when = days || (e.on_date ?? "");
    return [when, win].filter(Boolean).join(" ");
  }
  if (e.due_at) {
    const d = new Date(e.due_at);
    if (!Number.isNaN(d.getTime())) return `Due ${d.toLocaleString()}`;
  }
  return e.notes ?? "";
}

/** ISO timestamp → value for <input type="datetime-local"> (local time, no tz). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface EntrySeed {
  /** Prefill a one-off date (e.g. tapping a day in the grid). */
  on_date?: string;
  due_at?: string;
}

export function EntryForm({
  clanId = null,
  initial,
  seed,
  readOnly = false,
  onDone,
  onCancel,
}: {
  /** Non-null → create/edit a class-pushed entry (admin only, enforced server-side). */
  clanId?: string | null;
  /** Present → edit mode; scope (personal vs clan) is immutable. */
  initial?: CalendarEntry;
  /** Prefill values when creating from a grid day tap. */
  seed?: EntrySeed;
  /** View-only (e.g. a non-admin opening an inherited class entry). */
  readOnly?: boolean;
  onDone: () => void;
  /** When provided, renders a Cancel button next to Save. */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const isEditing = !!initial;
  const [kind, setKind] = useState<CalendarEntryKind>(
    initial?.entry_kind ?? (clanId ? "block" : "homework"),
  );
  const [title, setTitle] = useState(initial?.title ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [days, setDays] = useState<string[]>(initial?.days ?? []);
  const [start, setStart] = useState(initial?.start_time ?? "20:00");
  const [end, setEnd] = useState(initial?.end_time ?? "23:00");
  // Blocks are either weekly-recurring (days[]) or a one-off on a date.
  const [oneOff, setOneOff] = useState<boolean>(
    !!initial?.on_date || (!isEditing && !!seed?.on_date),
  );
  const [onDate, setOnDate] = useState<string>(initial?.on_date ?? seed?.on_date ?? "");
  const [due, setDue] = useState(
    isoToLocalInput(initial?.due_at ?? seed?.due_at ?? (seed?.on_date ? `${seed.on_date}T09:00` : null)),
  );
  const [setIds, setSetIds] = useState<string[]>(initial?.question_set_ids ?? []);
  const [err, setErr] = useState<string | null>(null);

  const setsQuery = useQuery({
    queryKey: ["question-sets"],
    queryFn: () => listQuestionSets(false),
    enabled: kind === "homework",
  });

  const save = useMutation({
    mutationFn: () => {
      const dueAt = due ? new Date(due).toISOString() : null;
      const blockDays = kind === "block" && !oneOff ? days : [];
      const blockOnDate = kind === "block" && oneOff ? onDate || null : null;
      if (isEditing && initial) {
        // Scope (personal vs clan) is fixed on create — PATCH carries no clan_id.
        return updateCalendarEntry(initial.id, {
          title: title.trim(),
          notes: notes.trim() || null,
          days: blockDays,
          start_time: kind === "block" ? start : null,
          end_time: kind === "block" ? end : null,
          on_date: blockOnDate,
          // Preserve the iOS-authored per-window app set; web never edits it.
          activity_selection: kind === "block" ? initial.activity_selection : null,
          question_set_ids: kind === "homework" ? setIds : [],
          due_at: dueAt,
        });
      }
      return createCalendarEntry({
        entry_kind: kind,
        title: title.trim(),
        notes: notes.trim() || null,
        clan_id: clanId,
        days: blockDays,
        start_time: kind === "block" ? start : null,
        end_time: kind === "block" ? end : null,
        on_date: blockOnDate,
        question_set_ids: kind === "homework" ? setIds : [],
        due_at: dueAt,
      });
    },
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof Error ? e.message : t("Couldn't save.")),
  });

  const remove = useMutation({
    mutationFn: () => deleteCalendarEntry(initial!.id),
    onSuccess: onDone,
    onError: (e) =>
      setErr(e instanceof Error ? e.message : t("Couldn't delete that entry. Please try again.")),
  });

  const canSave =
    !readOnly &&
    title.trim().length > 0 &&
    (kind !== "block" || (oneOff ? !!onDate : days.length > 0));

  return (
    <div className="card space-y-3 p-4">
      {err && <Alert>{err}</Alert>}
      {!isEditing && !readOnly && (
        <div className="inline-flex rounded-xl bg-black/5 p-1">
          {(["homework", "block", "task"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={cx(
                "rounded-lg px-3 py-1 text-sm font-semibold capitalize transition",
                kind === k ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
              )}
            >
              {t(k === "homework" ? "Homework" : k === "block" ? "Block" : "Task")}
            </button>
          ))}
        </div>
      )}
      <Input label={t("Title")} value={title} onChange={(e) => setTitle(e.target.value)} disabled={readOnly} />
      <Input label={t("Notes (optional)")} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={readOnly} />

      {kind === "block" && (
        <div className="space-y-2">
          {initial?.activity_selection && (
            <div className="flex flex-col gap-0.5">
              <span className="w-fit rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-semibold text-ink-soft">
                {t("Custom app set")}
              </span>
              <span className="text-[10px] text-ink-faint">{t("Configured on iOS — view only here.")}</span>
            </div>
          )}
          {/* Recurring vs one-off */}
          <div className="inline-flex rounded-xl bg-black/5 p-1 text-xs">
            {([["recurring", t("Repeats weekly")], ["oneoff", t("One-off on date")]] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                disabled={readOnly}
                onClick={() => setOneOff(k === "oneoff")}
                className={cx(
                  "rounded-lg px-2.5 py-1 font-semibold transition",
                  (oneOff ? "oneoff" : "recurring") === k ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {oneOff ? (
            <input
              type="date"
              value={onDate}
              disabled={readOnly}
              onChange={(e) => setOnDate(e.target.value)}
              className="rounded-lg border border-black/10 px-2 py-1 text-sm"
            />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  disabled={readOnly}
                  onClick={() => setDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]))}
                  className={cx(
                    "rounded-lg px-2 py-1 text-xs font-semibold",
                    days.includes(d) ? "bg-brand-600 text-white" : "bg-black/5 text-ink-soft",
                  )}
                >
                  {t(DAY_LABEL[d])}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="time" value={start} disabled={readOnly} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-black/10 px-2 py-1 text-sm" />
            <input type="time" value={end} disabled={readOnly} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-black/10 px-2 py-1 text-sm" />
          </div>
        </div>
      )}

      {kind === "homework" && (
        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Practice sets")}</span>
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-black/10 p-2">
            {setsQuery.data?.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={setIds.includes(s.id)}
                  disabled={readOnly}
                  onChange={(e) =>
                    setSetIds((p) => (e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id)))
                  }
                  className="h-4 w-4 rounded border-black/20 text-brand-600"
                />
                <span className="truncate">{s.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {(kind === "homework" || kind === "task") && (
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Due (optional)")}</span>
          <input
            type="datetime-local"
            value={due}
            disabled={readOnly}
            onChange={(e) => setDue(e.target.value)}
            className="rounded-lg border border-black/10 px-2 py-1 text-sm"
          />
        </label>
      )}

      <div className="flex gap-2">
        {!readOnly && (
          <Button
            size="sm"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => {
              setErr(null);
              save.mutate();
            }}
          >
            {t("Save")}
          </Button>
        )}
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            {readOnly ? t("Close") : t("Cancel")}
          </Button>
        )}
        {isEditing && !readOnly && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-red-600 hover:text-red-700"
            loading={remove.isPending}
            onClick={() => {
              if (window.confirm(t("Delete this entry?"))) {
                setErr(null);
                remove.mutate();
              }
            }}
          >
            {t("Delete")}
          </Button>
        )}
      </div>
    </div>
  );
}
